import { INTEGER_ITEM_PATTERN } from './fieldErrorCodes';
import { scadaItemIds } from './scadaItems';
import type { FieldValue, ProviderFieldSchema, ProviderSchema } from './types';

/** Client mirror of the server's cross-pen duplicate rule (duplicate-ids.ts). */
export interface DuplicateRow {
    cageId: number;
    isActive: boolean;
    values: Readonly<Record<string, FieldValue>>;
}

export interface DuplicateError {
    /** As the server reports it, e.g. `pens.101.external_unit_ids`. */
    path: string;
    code: 'duplicateExternalId';
}

/** Environmental readings are shown per pen, never summed, so one sensor may serve several pens (R12). */
const SHAREABLE_KEYS: ReadonlySet<string> = new Set([
    'signal_mappings.water_temperature',
    'signal_mappings.oxygen_saturation',
]);

/** Every Clarify signal shares one pool; SCADA ids are their own; unit lists pool per key. */
function poolOf(field: ProviderFieldSchema): string | null {
    if (field.scope !== 'pen') return null;
    switch (field.kind) {
        case 'clarifyItem':
        case 'scadaItems':
            return field.kind;
        case 'integerList':
        case 'stringList':
            return field.key;
        default:
            return null;
    }
}

/** R18: the sharing holds only under one key; the same item as another signal still collides. */
function shareKeyOf(field: ProviderFieldSchema): string | null {
    return field.kind === 'clarifyItem' && SHAREABLE_KEYS.has(field.key) ? field.key : null;
}

/** `05` and `5` are the same unit, so integers compare as numbers. */
function idsOf(field: ProviderFieldSchema, value: FieldValue): string[] {
    if (value === null) return [];
    if (field.kind === 'scadaItems') return scadaItemIds(value);
    const ids = (typeof value === 'string' ? [value] : value)
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '');
    if (field.kind !== 'integerList') return ids;
    return ids.map((id) => (INTEGER_ITEM_PATTERN.test(id) ? BigInt(id).toString() : id));
}

/** The pens holding one pooled id, grouped by the share key they hold it under. */
type PoolPens = Map<string | null, Set<number>>;

function hasRival(cages: PoolPens | undefined, cageId: number, shareKey: string | null): boolean {
    return [...(cages ?? new Map<string | null, Set<number>>())].some(
        ([key, pens]) =>
            (shareKey === null || key !== shareKey) && [...pens].some((other) => other !== cageId)
    );
}

interface Occurrence {
    cageId: number;
    path: string;
    poolId: string;
    shareKey: string | null;
}

/** Only active pens compete, and only touched pens are reported, as on the server. */
export function duplicateIdErrors(
    schema: ProviderSchema,
    rows: readonly DuplicateRow[],
    touched: ReadonlySet<number>
): DuplicateError[] {
    const pens = new Map<string, PoolPens>();
    const occurrences: Occurrence[] = [];
    for (const row of rows) {
        if (!row.isActive) continue;
        for (const field of schema.fields) {
            const pool = poolOf(field);
            if (pool === null) continue;
            const path = `pens.${row.cageId}.${field.key}`;
            const shareKey = shareKeyOf(field);
            for (const id of idsOf(field, row.values[field.key] ?? null)) {
                const poolId = `${pool}\u0000${id}`;
                const cages: PoolPens = pens.get(poolId) ?? new Map();
                const holders = cages.get(shareKey) ?? new Set<number>();
                holders.add(row.cageId);
                cages.set(shareKey, holders);
                pens.set(poolId, cages);
                occurrences.push({ cageId: row.cageId, path, poolId, shareKey });
            }
        }
    }
    const reported = new Set<string>();
    for (const { cageId, path, poolId, shareKey } of occurrences)
        if (touched.has(cageId) && hasRival(pens.get(poolId), cageId, shareKey)) reported.add(path);
    return [...reported].map((path) => ({ path, code: 'duplicateExternalId' }));
}
